import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { loadPixelBadges, usePixelBadges } from "@/lib/usePixelBadges";
import { STATUS } from "./PixelCheckRow";
import { PixelFixItem } from "./PixelFixItem";

const WORD = { fail: "Pixels broken", warn: "Pixels: check", ok: "Pixels OK" };
const BORDER = { fail: "border-red-600 text-red-700", warn: "border-amber-600 text-amber-800", ok: "border-emerald-700 text-emerald-800" };

// The same ID broken on the page and its thank-you page is one fix, so show it once.
function mergeItems(all) {
  const seen = new Map();
  const out = [];
  all.forEach((it) => {
    const k = it.fix?.inline ? `${it.fix.where}:${it.fix.page_id}:${it.fix.field}` : null;
    if (k && seen.has(k)) {
      const first = out[seen.get(k)];
      first.text = first.text.replace(/^(Page|Thank-you): /, "Page + thank-you: ");
      return;
    }
    if (k) seen.set(k, out.length);
    out.push({ ...it });
  });
  return out;
}

// Small pixel status for one live page; tap for its problems and "Fix it". Hidden for drafts and users without pixel access.
export function PixelBadge({ pageId, testId, onFixed }) {
  const d = usePixelBadges();
  const [open, setOpen] = useState(false);
  const b = d?.pages?.[pageId];
  if (!b) return null;
  const [Icon, cls] = STATUS[b.status];
  const tid = testId || `pixel-badge-${pageId}`;
  const items = mergeItems([...d.main_problems.map((p) => ({ ...p, text: `Main pixels: ${p.text}` })), ...b.problems, ...b.live]);
  const done = (r) => { loadPixelBadges(true); onFixed?.(r); };
  const close = () => setOpen(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={tid}
                className={`inline-flex items-center gap-1 whitespace-nowrap border bg-white px-1.5 py-1 text-[9px] font-bold uppercase tracking-widest transition-colors duration-100 hover:bg-zinc-100 ${BORDER[b.status]}`}>
          <Icon size={11} weight="fill" className={cls} /> {WORD[b.status]}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={8} className="w-80 max-w-[calc(100vw-16px)] rounded-none border-2 border-black p-3 text-xs space-y-2" data-testid={`${tid}-popup`}>
        <div className={`font-bold ${cls}`}>{WORD[b.status]}</div>
        {items.length ? (
          <ul className="space-y-2" data-testid={`${tid}-problems`}>
            {items.map((it, i) => (
              <PixelFixItem key={it.text} text={it.text} fix={it.fix} tid={`${tid}-item-${i}`} onDone={done} onNavigate={close} />
            ))}
          </ul>
        ) : <p data-testid={`${tid}-ok`}>Main pixels and this page&apos;s extra pixels are set up and nothing failed the last live test.</p>}
        <p className="text-[10px] text-zinc-500">{d.live_at ? `Last live test ${new Date(d.live_at).toLocaleString()}` : "No live test yet"}</p>
        <Link to={`/admin/pixels?page=${pageId}`} onClick={close} className="inline-block font-bold underline" data-testid={`${tid}-open`}>Open in Pixel check</Link>
      </PopoverContent>
    </Popover>
  );
}
